/**
 * Daily Reward Policy Helpers
 *
 * Centralizes all daily-reward eligibility rules so that signup flows,
 * payment webhooks, and the claim endpoint share the same logic.
 */

/** Duration of the free-bonus window after signup or purchase. */
export const FREE_BONUS_WINDOW_DAYS = 7;

/**
 * Non-payers whose credit balance is at or above this threshold
 * are blocked from claiming further free rewards.
 */
export const NON_PAYER_FREE_CLAIM_THRESHOLD = 21;

/**
 * Returns an ISO-8601 timestamp representing `now + 7 days`.
 * Use this when creating or resetting the dailyReward.endDate (eligible_until semantics).
 */
export function getFreeBonusEligibleUntil(now: Date = new Date()): string {
  return new Date(
    now.getTime() + FREE_BONUS_WINDOW_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();
}

/**
 * Returns `true` when the free-bonus window is still open.
 * Uses `<=` so the exact boundary moment is still active.
 * Returns `false` when endDate is missing/null or has passed.
 */
export function isFreeBonusWindowActive(
  endDate: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!endDate) return false;
  return now <= new Date(endDate);
}

/** Returns true when a non-payer has credits >= threshold. */
export function isNonPayerThresholdBlocked(
  purchasesCount: number,
  credits: number
): boolean {
  return purchasesCount === 0 && credits >= NON_PAYER_FREE_CLAIM_THRESHOLD;
}

/**
 * Returns the reason a user is blocked from claiming, or null if they can claim.
 * Used by both the server claim endpoint and the client modal to ensure consistent behavior.
 */
export function getDailyRewardBlockReason(
  endDate: string | null | undefined,
  purchasesCount: number,
  credits: number,
  now: Date = new Date()
): {
  reasonCode: "WINDOW_EXPIRED" | "NON_PAYER_THRESHOLD";
  message: string;
} | null {
  if (!isFreeBonusWindowActive(endDate, now)) {
    return {
      reasonCode: "WINDOW_EXPIRED",
      message:
        "Free daily bonuses are available for 7 days after signup or purchase.",
    };
  }
  if (isNonPayerThresholdBlocked(purchasesCount, credits)) {
    return {
      reasonCode: "NON_PAYER_THRESHOLD",
      message: "Use your credits first to claim more free credits.",
    };
  }
  return null;
}
